import React, { MouseEventHandler } from 'react';
import { DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { IntlShape, injectIntl } from 'react-intl';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Typography, IconButton } from '@material-ui/core';
import { BUTTON_COLORS, COLORS } from 'constants/design';
import CheckedIcon from 'components/SahamIcons/CheckedIcon';
import routesHelp from 'containers/services/routes-helper';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import { StyledDialog } from 'components/SuccessDialog/styled-components';
import { PAGES } from 'routes';
import { translate } from 'containers/services/translate';
import messages from 'containers/LawyerFeeNote/messages';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';

export interface SuccessDialogProps {
  intl: IntlShape;
  open: boolean;
  onClose: Function;
  title: string;
  showIcon?: boolean;
  primaryButtonTitle?: string;
  secondaryButtonTitle: string;
  onPrimaryClick?: MouseEventHandler;
  tertiaryButtonTitle?: string;
  onTertiaryClick?: MouseEventHandler;
  homePath: string;
  isExpertReport: boolean;
  isPrimaryButtonDisabled?: boolean;
}

const SuccessDialog: React.FC<SuccessDialogProps> = (props): JSX.Element => {
  const {
    onClose,
    open,
    tertiaryButtonTitle,
    onTertiaryClick,
    showIcon = true,
  } = props;

  const handleClose = () => {
    onClose(false);
  };

  const resolveMessage = () => {
    return featureService.hasFeature(FEATURES.FEE_NOTE) && !props.isExpertReport
      ? translate({ ...messages.feeNoteRedirection }, props)
      : translate({ ...messages.successDialogPrimaryButtonTitle }, props);
  };

  return (
    <StyledDialog onClose={handleClose} open={open}>
      <DialogTitle>
        {showIcon && (
          <IconButton
            data-testid="success-dialog-close-button"
            className="success-close-icon"
            onClick={handleClose}
          >
            <HighlightOffIcon className="HighlightOffIcon" />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent className="success-content">
        <CheckedIcon width="47.5px" height="47.5px" color={COLORS.CHECKED} />
        <Typography className="success-title">{props.title}</Typography>
      </DialogContent>
      <DialogActions className="success-actions">
        <PrimaryButton
          onClick={
            props.onPrimaryClick
              ? props.onPrimaryClick
              : routesHelp.pushTo(PAGES.FEE_NOTE)
          }
          title={
            props.primaryButtonTitle
              ? props.primaryButtonTitle
              : resolveMessage()
          }
          righticon={<ChevronRightIcon className="ChevronRightIcon" />}
          background={BUTTON_COLORS.PRIMARY_BLUE}
          border={BUTTON_COLORS.PRIMARY_BLUE}
          titlecolor={BUTTON_COLORS.BLUE_BUTTON_TITLE}
          data-testid="generate-fee-note"
          disabled={props.isPrimaryButtonDisabled && !props.isExpertReport}
        />
        <span style={{ color: COLORS.RED }}>
          {props.isPrimaryButtonDisabled &&
            translate({ ...messages.firmReferenceWarning }, props)}
        </span>
        {tertiaryButtonTitle && onTertiaryClick && (
          <PrimaryButton
            onClick={onTertiaryClick}
            title={tertiaryButtonTitle}
            righticon={<ChevronRightIcon className="ChevronRightIcon" />}
            background={BUTTON_COLORS.PRIMARY_BLUE}
            border={BUTTON_COLORS.PRIMARY_BLUE}
            titlecolor={BUTTON_COLORS.BLUE_BUTTON_TITLE}
            data-testid="not-generate-fee-note"
          />
        )}
        <SecondaryButton
          onClick={routesHelp.pushTo(props.homePath)}
          title={props.secondaryButtonTitle}
        />
      </DialogActions>
    </StyledDialog>
  );
};

export default injectIntl(SuccessDialog);
